import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { IComment } from '@app/eds/models/comment.model';
import { ModalService } from '@app/eds/services/modal/modal.service';
import {
  ModalPopupIds,
  SharedConstants,
  SharedCssClassNames,
  TextareaComponent,
  TextStyleTypes,
  TextType
} from '@app/eds';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-comment[commentData]',
  templateUrl: './comment.component.html',
  styleUrls: ['./comment.component.scss']
})
export class CommentComponent {
  // commentData provides comment info of IComment type
  @Input() commentData!: IComment;

  // check whether the comment is editable or not (in case of the parent comments)
  @Input() isCommentEditable = false;

  // originalComment to store the original comment while editing the comment.
  originalComment!: string;

  // isOnClick provides whether the textarea clicked or not
  isOnClick = false;

  // deleteComment provides info about which comment to be deleted
  deleteComment!: IComment | null;

  // editTextAreaWidth provides textArea width
  @Input() editTextAreaWidth!: string;

  // used for toggling edit btn
  @Input() showEditBtn = true;

  // used for toggling delete btn
  @Input() showDeleteBtn = true;

  // updatedComment to  store new comment
  updatedComment: string = SharedConstants.EMPTY_STRING;

  // selectedComment provides info about which comment is being selected to edit
  selectedComment!: IComment | null;

  // DeleteButtonClicked emits an event when deleted button is being clicked.
  @Output() deleteButtonClicked: EventEmitter<IComment> = new EventEmitter<IComment>();

  // EditButtonClicked emits an event when the edit button is being clicked.
  @Output() editButtonClicked: EventEmitter<IComment> = new EventEmitter<IComment>();

  // SaveChangesButtonClicked emits an event when save changes button is being clicked.
  @Output() saveChangesButtonClicked: EventEmitter<IComment> =
    new EventEmitter<IComment>();

  // for edit comment
  @ViewChild('editCommentTextArea') childTextArea!: TextareaComponent;

  // for delete comment modal
  @ViewChild('deleteCommentDialogRef')
  deleteCommentDialogRef!: TemplateRef<any>;

  // Used to accept custom class for displaying comment.
  @Input() commentMessageClass: string = SharedCssClassNames.COMMENT_MSG;

  // Used to accept custom class for text style.
  @Input() commentMessageTextStyle: TextType = TextStyleTypes.BODY2;

  constructor(
    private readonly translate: TranslateService,
    private readonly modalService: ModalService
  ) { }

  /**
   * This function will call on clicking textarea.
   */
  checkIsOnClick = () => (this.isOnClick = true);

  /**
   * This function checks whether the comment is in edit mode or readMode.
   *
   * @param {boolean}
   */
  checkIsEditMode = () =>
    this.commentData === this.selectedComment && this.commentData.editMode;

  /**
   * This function will trigger from child component to get data from textarea.
   *
   * @param {args} is an event
   */
  getUpdatedComment = (args: any) => {
    this.isOnClick = true;
    this.updatedComment = args.target.value.trim();
  };

  /**
   * This method will help to return button type.
   *
   * @returns {string}
   */
  getButtonType = () =>
    this.updatedComment.length && this.updatedComment !== this.originalComment
      ? 'primary'
      : 'primaryDisabled';

  /**
   * This function will set value to textarea
   *
   * @param {string} is the  comment msg
   */
  setValueToTextArea = (textAreaInput: string) => {
    this.updatedComment = textAreaInput.trim();
    this.originalComment = this.updatedComment;
    this.childTextArea.inputText.setValue(textAreaInput);
  };

  /**
   * This function will call when save changes button is being clicked.
   * This will emit an event.
   *
   * @param {IComment} is the comment details
   */
  updateComment = (commentData: IComment) => {
    this.saveChangesButtonClicked.emit(commentData);
    this.selectedComment = null;
  };

  /**
   * This function will call on clicking cancel button.
   */
  onCancel = () => {
    this.isOnClick = false;
    this.selectedComment = null;
  };

  /**
   * This function will call when Delete button is being clicked.
   *
   * @param {IComment} is the comment details
   */
  onDeleteClick = (commentData: IComment) => {
    this.deleteComment = commentData;
    this.modalService.openModalFromCenter({
      template: this.deleteCommentDialogRef,
      id: ModalPopupIds.DELETE_COMMENT_MODAL,
      width: '43.75em',
      modalTitle: this.translate.instant(`comment.deleteCommentPopupModal.title`)
    });
  };

  /**
   * This function will call when cancel button of delete popup is being clicked.
   */
  onCancelCommentDelete = () => {
    this.modalService.closeModalById(ModalPopupIds.DELETE_COMMENT_MODAL);
  };

  /**
   * This function will call when confirm delete button of delete popup is being clicked.
   * This will emit an event.
   */
  onConfirmCommentDelete = () => {
    if (this.deleteComment) {
      this.deleteButtonClicked.emit(this.deleteComment);
      this.modalService.closeModalById(ModalPopupIds.DELETE_COMMENT_MODAL);
    }
  };

  /**
   * This function will call when edit button is being clicked.
   *
   * @param {IComment} selectedComment is the comment details
   */
  onEditClick = (selectedComment: IComment) => {
    this.selectedComment = selectedComment;
    setTimeout(() => {
      this.setValueToTextArea(selectedComment.description);
    });
    this.editButtonClicked.emit(this.selectedComment);
  };

  /**
   * This function will call when save changes button is being clicked.
   *
   * @param {IComment} commentData is the updated comment details
   */
  onSaveChanges = (commentData: IComment) => {
    if (this.updatedComment && this.updatedComment !== this.originalComment) {
      commentData.description = this.updatedComment;
      this.updateComment(commentData);
    }
  };

  /**
   * This method will help to return custom textarea style.
   *
   * @returns {string}
   */
  getTextAReaStyle = () => this.editTextAreaWidth;

  /**
   * This method will return PO tag text for app-chip.
   * Ex - PO 1234567
   *
   * @returns {string}
   */
  getPOtagText = () =>
    this.translate.instant(`asn.po`) + ` ${this.commentData.purchaseOrderId}`;

  /**
   * This method will help to check whether a comment is editable or not
   *
   * @param {IComment} commentData
   * @returns {boolean}
   */
  isCommentsEditable = (commentData: IComment) =>
    this.isCommentEditable || (commentData.isEditable && commentData.appointmentId);
}
